// Hideo - script editor's writing surface: the commands on the text (toolbar, "/" menu, selection menu, block menu):
// lists and checklists, colours, size, line spacing, links, block types, fonts, and the toolbar's state under the
// cursor. Ported from the legacy editor/editor.js with the same steps and the same browser commands.
import { t } from '../../../i18n'
import { esc } from '../../../utils/html'
import { normUrl } from '../../../utils/url'
import { uiPrompt } from '../../../stores/notices'
import { useToolbarStore } from '../../../stores/toolbar'
import { fontCss } from '../styles'
import { edBlockOf, edBody, edCaret, ex, listKind, queueScriptSave, restoreSel, savedRange, selList, setSavedRange } from './core'
import { fixLinks } from '../html'

// Checklist: ul.todo with ticked items marked by data-done; the box is drawn by the CSS (li::before), so the list behaves
// like a bulleted list (Enter twice = exit). fixTodos (html.ts) converts the old <input> checkboxes of older saved scripts.
/** Toggles the checklist on the selection */
export function toggleTodo(): void {
  const l = selList()
  if (l && l.tagName === 'UL' && l.classList.contains('todo')) { listOff(l); return }   // "Checklist" again = remove the list
  if (l && l.tagName === 'UL') { l.className = 'todo'; return queueScriptSave() }
  ex('insertUnorderedList')
  const l2 = selList(); if (l2) l2.className = 'todo'; queueScriptSave()
}
/** A point of the selection given by an element (the cursor after a click at the end of a line…) as a point in its
 *  text, so it stays where it is when the elements around the text change */
function textPoint(n: Node | null, o: number): [Node | null, number] {
  while (n && n.nodeType === 1 && n.childNodes.length) {
    const end = o >= n.childNodes.length
    n = n.childNodes[end ? n.childNodes.length - 1 : o]
    o = end ? (n.nodeType === 3 ? (n.textContent as string).length : n.childNodes.length) : 0
  }
  return [n, o]
}
/** Removes the list from the selected items (or the one with the cursor): they become paragraphs, the rest of the list
 *  stays. Done by hand: the browser command left <span style> and <br> instead of paragraphs. Returns the paragraphs. */
export function listOff(l: HTMLElement): HTMLElement[] {
  const s = getSelection() as Selection, r = s.rangeCount ? s.getRangeAt(0) : null, an = s.anchorNode, ao = s.anchorOffset
  const kids = [...l.children], items = kids.filter(li => r && r.intersectsNode(li))
  if (!items.length) return []
  const first = kids.indexOf(items[0]), after = kids.slice(kids.indexOf(items[items.length - 1]) + 1)
  const ps = items.map(li => { const p = document.createElement('p'); p.append(...li.childNodes); if (!p.firstChild) p.innerHTML = '<br>'; return p })
  const out: Element[] = [...ps]
  if (after.length) { const l2 = l.cloneNode(false) as HTMLElement; l2.append(...after); out.push(l2) }
  if (first > 0) { items.forEach(li => li.remove()); l.after(...out) } else l.replaceWith(...out)
  if (an && document.contains(an)) edCaret(an, ao)
  queueScriptSave()
  return ps
}
/** Quote and lists never go one inside the other (the browser's commands put a list inside the quote, and Enter then
 *  left the quote): "Quote" on list items makes one quote of them, a line each; "Quote" on a quote takes it away, a
 *  paragraph per line. Done by hand; false when the selection is not in a list or a quote (the browser's command). */
function quoteByHand(): boolean {
  const s = getSelection() as Selection
  if (!s.rangeCount) return false
  const [an, ao] = textPoint(s.anchorNode, s.anchorOffset), l = selList(), blk = edBlockOf(an)
  if (l && l.parentElement === edBody()) {
    const ps = listOff(l), q = document.createElement('blockquote')
    ps.forEach((p, i) => { if (i) q.append(document.createElement('br')); q.append(...[...p.childNodes].filter(n => !(n.nodeName === 'BR' && !n.nextSibling))) })
    if (!q.lastChild) q.innerHTML = '<br>'
    ps[0].before(q); ps.forEach(p => p.remove())
    if (!(an && an.isConnected && an.nodeType === 3)) edCaret(q, q.childNodes.length)
  } else if (blk && blk.tagName === 'BLOCKQUOTE' && blk === edBlockOf(s.focusNode)) {
    const lines: Node[][] = [[]]
    blk.childNodes.forEach(n => { if (n.nodeName === 'BR') lines.push([]); else lines[lines.length - 1].push(n) })
    const ps = lines.map(ln => { const p = document.createElement('p'); p.append(...ln); if (!p.firstChild) p.innerHTML = '<br>'; return p })
    blk.replaceWith(...ps)
    if (!(an && an.isConnected && an.nodeType === 3)) edCaret(ps[0], 0)
  } else return false
  if (an && an.isConnected && an.nodeType === 3) edCaret(an, ao)
  queueScriptSave(); return true
}
/** List picked from the menu: bulleted / numbered / checklist ('ul' / 'ol' / 'todo'), starting from any list */
export function setListKind(k: string): void {
  const l = selList()
  if (l) {   // from one list to another: done by hand (the browser copied the tick's strikethrough and colours into the text)
    const cur = listKind(l)
    if (cur === k) { listOff(l); return }   // same kind = remove the list
    const s = getSelection() as Selection, an = s.anchorNode, ao = s.anchorOffset
    const nl = document.createElement(k === 'ol' ? 'ol' : 'ul'); if (k === 'todo') nl.className = 'todo'
    ;[...l.childNodes].forEach(li => { if (k !== 'todo' && li.nodeType === 1) (li as Element).removeAttribute('data-done'); nl.appendChild(li) })
    l.replaceWith(nl); if (an && nl.contains(an)) edCaret(an, ao)
    return queueScriptSave()
  }
  const s = getSelection() as Selection, blk = s.rangeCount ? edBlockOf(s.anchorNode) : null
  if (blk && blk === edBlockOf(s.focusNode) && (/^(P|DIV)$/.test(blk.tagName) && !blk.className || blk.tagName === 'BLOCKQUOTE')) {   // one paragraph or quote: by hand
    const r = s.getRangeAt(0), a = textPoint(r.startContainer, r.startOffset) as [Node, number], b = r.collapsed ? a : textPoint(r.endContainer, r.endOffset) as [Node, number]
    const z = blockToList(blk, k), list = (z.parentNode as HTMLElement).parentNode as HTMLElement, last = list.lastElementChild as HTMLElement
    // the selection stays where it was (a point given by the block itself: the start or the end of the list)
    const at = ([n, o]: [Node, number], end: boolean): [Node, number] => n !== blk && list.contains(n) ? [n, o] : end && o ? [last, last.childNodes.length] : [z, 1]
    const nr = document.createRange(); nr.setStart(...at(a, false)); nr.setEnd(...at(b, true))
    s.removeAllRanges(); s.addRange(nr); setSavedRange(nr.cloneRange())
    return queueScriptSave()
  }
  if (k === 'todo') return toggleTodo()
  ex(k === 'ol' ? 'insertOrderedList' : 'insertUnorderedList')
}
/** A paragraph becomes a one-item list with its content, a quote a list with an item per line, by hand: the browser's
 *  command put the list inside the paragraph or the quote (and Safari left the text out of the item). Returns the
 *  zero-width space at the start of the first item. */
export function blockToList(blk: HTMLElement, k: string): Text {
  const list = document.createElement(k === 'ol' ? 'ol' : 'ul')
  if (k === 'todo') list.className = 'todo'
  const lines: Node[][] = [[]]
  blk.childNodes.forEach(n => { if (n.nodeName === 'BR' && blk.tagName === 'BLOCKQUOTE') lines.push([]); else lines[lines.length - 1].push(n) })
  lines.forEach((kids, i) => {
    if (kids.length && kids[kids.length - 1].nodeName === 'BR') kids.pop()   // the empty line's <br>
    if (i && i === lines.length - 1 && !kids.length) return   // a quote ending with a line break
    const li = document.createElement('li'); li.append(...kids)
    if (!li.firstChild) li.innerHTML = '<br>'
    list.appendChild(li)
  })
  const z = document.createTextNode('\u200b'); (list.firstElementChild as HTMLElement).prepend(z)
  const first = list.firstElementChild as HTMLElement
  if (first.firstChild === z && z.nextSibling && z.nextSibling.nodeName === 'BR' && !z.nextSibling.nextSibling) (z.nextSibling as ChildNode).remove()
  blk.replaceWith(list); return z
}

/** "Clear formatting" (toolbar and selection menu): styles and links of the selection */
export function clearFormat(): void { ex('removeFormat'); ex('unlink') }
/** All caps / all lowercase (AB / ab): the selected letters themselves change, inside every formatting; the selection
 *  stays on them. Nothing without a selection. Written in the text nodes directly: the browser's insertText moved
 *  the text into the formatting next to it and lost the selection (so Undo does not take it back; ab / AB does). */
export function setCase(upper: boolean): void {
  const body = edBody()
  body.focus(); restoreSel()
  const s = getSelection() as Selection
  if (!s.rangeCount) return
  const r = s.getRangeAt(0), root = r.commonAncestorContainer
  if (r.collapsed || !body.contains(root)) return
  const nodes: Text[] = []
  if (root.nodeType === 3) nodes.push(root as Text)
  else { const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT); while (w.nextNode()) if (r.intersectsNode(w.currentNode)) nodes.push(w.currentNode as Text) }
  const { startContainer: sc, startOffset: so, endContainer: ec } = r
  let eo = r.endOffset
  nodes.forEach(n => {
    const a = n === sc ? so : 0, b = n === ec ? eo : n.data.length, mid = n.data.slice(a, b)
    const to = upper ? mid.toUpperCase() : mid.toLowerCase()   // the length can change (ß → SS)
    if (to === mid) return
    n.data = n.data.slice(0, a) + to + n.data.slice(b)
    if (n === ec) eo = a + to.length
  })
  const nr = document.createRange(); nr.setStart(sc, so); nr.setEnd(ec, eo)
  s.removeAllRanges(); s.addRange(nr); setSavedRange(nr.cloneRange())
  queueScriptSave()
}
/** Applies a colour and shows it under its button (bar: 'tcBar' / 'hlBar', the line under the button) */
export function applyColor(cmd: string, val: string, bar: 'tcBar' | 'hlBar'): void { useToolbarStore().tb[bar] = val; ex(cmd, val) }
/** Text size of the selection (8–120 px, 17 when not a number). The resized text stays selected, so − / + and the
 *  field can be used again and again on it (before, the selection was lost and the field went back to 17); sizes
 *  inside it are replaced, and a span that only wrapped it takes the new size instead of nesting another one. */
export function setFontSize(px: number | string): void {
  const n = Math.max(8, Math.min(120, +px || 17)), body = edBody()
  body.focus(); restoreSel()
  document.execCommand('styleWithCSS', false, false as unknown as string); document.execCommand('fontSize', false, '7'); document.execCommand('styleWithCSS', false, true as unknown as string)
  const spans: HTMLElement[] = []
  body.querySelectorAll('font[size="7"]').forEach(f => {
    let sp = document.createElement('span'); sp.style.fontSize = n + 'px'; sp.append(...f.childNodes); f.replaceWith(sp)
    sp.querySelectorAll<HTMLElement>('[style]').forEach(x => { x.style.fontSize = ''; if (!x.getAttribute('style')) x.removeAttribute('style'); if (x.tagName === 'SPAN' && !x.attributes.length) x.replaceWith(...x.childNodes) })
    const up = sp.parentElement
    if (up && up !== body && up.tagName === 'SPAN' && up.childNodes.length === 1) { up.style.fontSize = n + 'px'; sp.replaceWith(...sp.childNodes); sp = up }
    spans.push(sp)
  })
  if (spans.length) {
    const r = document.createRange(), last = spans[spans.length - 1]
    r.setStart(spans[0], 0); r.setEnd(last, last.childNodes.length)
    const s = getSelection() as Selection; s.removeAllRanges(); s.addRange(r); setSavedRange(r.cloneRange())
  }
  useToolbarStore().tb.fsize = n; queueScriptSave()
}
/** The size field's − / + */
export function stepSize(d: number): void { setFontSize((+useToolbarStore().tb.fsize || 17) + d) }
/** Line spacing of the selected blocks */
export function setLineHeight(v: string): void {
  if (!v || !savedRange) return
  const r = savedRange
  ;[...edBody().children].forEach(c => { if (r.intersectsNode(c)) (c as HTMLElement).style.lineHeight = v })
  queueScriptSave()
}
/** Inserts a link (asks the address): on the selected text, or the address itself followed by a space */
export async function insertLink(): Promise<void> {
  restoreSel()
  const txt = (getSelection() as Selection).toString(), keep = savedRange && savedRange.cloneRange()
  const url = normUrl(await uiPrompt(t('Link address (URL):'), 'https://', { ok: t('Insert'), icon: 'fa-link' }) || '')
  if (keep) setSavedRange(keep)   // the dialog moves the cursor: go back to the previous selection
  if (!url || url === 'https://' || /^javascript:/i.test(url)) return
  const body = edBody()
  body.focus(); restoreSel()
  if (txt) document.execCommand('createLink', false, url); else document.execCommand('insertHTML', false, `<a href="${esc(url)}">${esc(url)}</a>&nbsp;`)
  fixLinks(body)
  queueScriptSave()
}
/** A block type, list, divider, link, toggle or callout on the selection */
export function applyBlock(v: string): void {
  if (!v) return
  if (v === 'blockquote') { edBody().focus(); restoreSel(); if (quoteByHand()) return }
  if (['p', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'pre'].includes(v)) return ex('formatBlock', '<' + v + '>')
  if (v === 'ul' || v === 'ol' || v === 'todo') { edBody().focus(); restoreSel(); return setListKind(v) }
  if (v === 'hr') return ex('insertHorizontalRule')
  if (v === 'link') { void insertLink(); return }
  const h = ({ toggle: `<details open><summary>${t('Title')}</summary><p>${t('Content')}</p></details><p><br></p>`, callout: `<div class="callout"><p>${t('Text')}</p></div><p><br></p>` } as Record<string, string>)[v]
  if (h) ex('insertHTML', h)
}
/** "Normal colour": removes the text colour from the elements touched by the selection (bold, headings etc. stay) */
export function clearTextColor(): void {
  const r = savedRange
  if (!r) return
  edBody().querySelectorAll<HTMLElement>('[style*="color"], font[color]').forEach(el => {
    if (!r.intersectsNode(el)) return
    el.style.removeProperty('color'); el.removeAttribute('color')
    if (!el.getAttribute('style')) el.removeAttribute('style')
  })
  queueScriptSave()
}

/** Alignment and list where the cursor is (the toolbar's drop-down buttons) */
export function tbState(): { align: string; list: string } {
  const l = selList(), q = (c: string) => { try { return document.queryCommandState(c) } catch { return false } }
  return {
    align: ['justifyCenter', 'justifyRight', 'justifyFull'].find(q) || 'justifyLeft',
    list: l ? listKind(l) : ''
  }
}

/** Applies the font (and weight) to the selection; with no selection, to the word under the cursor (the Font menu) */
export function applyFont(fam: string, w: number): void {
  const tb = useToolbarStore()
  tb.hideDrop()
  const body = edBody()
  body.focus(); restoreSel()
  const s = getSelection() as Selection
  if (!s.rangeCount || !body.contains(s.getRangeAt(0).commonAncestorContainer)) return
  if (s.isCollapsed) { try { s.modify('move', 'backward', 'word'); s.modify('extend', 'forward', 'word') } catch { /* not supported */ } }
  if (s.isCollapsed) { document.execCommand('fontName', false, fam); return queueScriptSave() }   // empty line: applies to what you type next
  document.execCommand('styleWithCSS', false, true as unknown as string)
  document.execCommand('fontName', false, 'hideo-font-mark')
  body.querySelectorAll('font[face="hideo-font-mark"]').forEach(f => { const sp = document.createElement('span'); sp.style.fontFamily = 'hideo-font-mark'; sp.append(...f.childNodes); f.replaceWith(sp) })
  body.querySelectorAll<HTMLElement>('[style*="hideo-font-mark"]').forEach(el => {
    el.style.fontFamily = fontCss(fam)
    if (w) { el.style.fontWeight = String(w); el.querySelectorAll<HTMLElement>('[style*="font-weight"]').forEach(x => x.style.removeProperty('font-weight')) }
    el.querySelectorAll<HTMLElement>('[style*="font-family"]').forEach(x => x.style.removeProperty('font-family'))
  })
  if (s.rangeCount) setSavedRange(s.getRangeAt(0).cloneRange())
  queueScriptSave(); tb.updState()
}
